import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs';
import packageJson from 'src/../package.json';
import { AuthService } from '@core/services/auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);

  public version: string = packageJson.version;

  backgroundImage: string = 'assets/images/backgrounds/cloud-security.jpg';

  private backgroundImages: string[] = [
    'assets/images/backgrounds/cloud-security.jpg',
    'assets/images/backgrounds/cyber-security-solutions.jpg',
    'assets/images/backgrounds/technology-services.jpg',
    'assets/images/backgrounds/datacenter.png',
  ];

  constructor() {
    // SSR: Skip crypto random on server, use default background
    if (isPlatformBrowser(this.platformId)) {
      // Use cryptographically secure random for background selection
      const randomArray = new Uint32Array(1);
      window.crypto.getRandomValues(randomArray);
      const randomIndex = randomArray[0] % this.backgroundImages.length;
      this.backgroundImage = this.backgroundImages[randomIndex];
    }
  }

  ngOnInit(): void {
    void this.checkIfUserIsAuthenticated();

    // Redirect-return path: Firebase processes the OAuth code asynchronously (network round-trip).
    // If processing takes longer than waitForHydratedUser's 2s timeout, the check above returns
    // null and the user stays stuck on /login. This subscription catches the user whenever auth
    // resolves, regardless of how long Firebase takes.
    this.authService.currentUser.pipe(
      filter(user => !!user?.['uid']),
      take(1)
    ).subscribe(() => {
      void this.router.navigate(['/logging-in'], { replaceUrl: true });
    });
  }

  /**
   * Redirects authenticated users into the shared post-login flow.
   */
  private async checkIfUserIsAuthenticated(): Promise<void> {
    try {
      const user = await this.authService.waitForHydratedUser(2000);
      if (user) {
        await this.router.navigate(['/logging-in'], { replaceUrl: true });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  }
}
